import React, {ReactInstance} from 'react';
import {inject, observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {IDesignable} from '@techsee/techsee-ui-common/lib/_shared/reusable-props';
import {SimpleTabs} from '@techsee/techsee-ui-common/lib/tabs/simple';
import {SecondaryButton} from '@techsee/techsee-ui-common/lib/forms/buttons/secondary';
import {Icon} from '@techsee/techsee-ui-common/lib/icons/simple-icon';
import {PrimaryButton} from '@techsee/techsee-ui-common/lib/forms/buttons/primary';
import {AddressTypesEnum, MeetingMode} from '@techsee/techsee-common/lib/constants/room.constants';

import {
    AgentGuidanceItem,
    GuidanceProgress,
    GuidanceProgressProps,
    GuidenceProgressItem
} from '../components/guidance-progress/GuidanceProgress';
import {ITranslate} from '../../../../services/LocalizationService';
import {IInviteWizardController, InviteDeviceType} from '../../_contracts/InviteContracts';
import {Nullable} from '@techsee/techsee-common';
import ReactDOM from 'react-dom';

export function isScreenOrAppMirroring(meetingMode?: MeetingMode): boolean {
    return meetingMode === MeetingMode.screen || meetingMode === MeetingMode.appSharing;
}

export function buildWaitingGuidanceItems(
    stepType: string,
    deviceType: InviteDeviceType,
    translate: (key: string) => string,
    skipFirstStep?: boolean
): AgentGuidanceItem[] {
    const isIos = deviceType === InviteDeviceType.ios;
    const guidanceItems: AgentGuidanceItem[] = [];

    if (!skipFirstStep) {
        guidanceItems.push({
            stepType,
            stepNum: 1,
            stepText: translate('REACT.SMS.WAITING_FOR_CLIENT_ANDROID_INSTRUCTION_ONE'),
            deviceType,
            tooltipPosition: {
                top: isIos ? '43%' : '39%',
                left: isIos ? '11%' : '19%'
            }
        });
    }

    guidanceItems.push(
        {
            stepType,
            stepNum: 2,
            ...(skipFirstStep && {displayStepNum: 1}),
            stepText: translate(
                isIos
                    ? 'REACT.SMS.WAITING_FOR_CLIENT_IOS_INSTRUCTION_TWO'
                    : 'REACT.EMAIL.REDIRECTED_TO_STORE_INSTRUCTION_ONE'
            ),
            deviceType,
            tooltipPosition: {
                top: isIos ? '60%' : '50%',
                left: isIos ? '14%' : '7%'
            },
            maxHeight: '53px'
        },
        {
            stepType,
            stepNum: 3,
            ...(skipFirstStep && {displayStepNum: 2}),
            stepText: translate(
                isIos
                    ? 'REACT.SMS.WAITING_FOR_CLIENT_IOS_INSTRUCTION_THREE'
                    : 'REACT.SMS.WAITING_FOR_CLIENT_ANDROID_INSTRUCTION_THREE'
            ),
            deviceType,
            tooltipPosition: {
                top: isIos ? '43%' : '23%',
                left: isIos ? '0%' : '-4%'
            },
            maxHeight: '53px'
        }
    );

    if (isIos) {
        guidanceItems.push(
            {
                stepType,
                stepNum: 4,
                ...(skipFirstStep && {displayStepNum: 3}),
                stepText: translate('REACT.SMS.WAITING_FOR_CLIENT_IOS_INSTRUCTION_FOUR'),
                deviceType,
                tooltipPosition: {
                    top: '60%',
                    left: '-1%'
                }
            },
            {
                stepType,
                stepNum: 5,
                ...(skipFirstStep && {displayStepNum: 4}),
                stepText: translate('REACT.SMS.WAITING_FOR_CLIENT_IOS_INSTRUCTION_FIVE'),
                deviceType,
                tooltipPosition: {
                    top: '20%',
                    left: '5%'
                }
            }
        );
    }

    return guidanceItems;
}

interface IStepLayoutProps extends IDesignable {
    h1Text: string;
    h2Text: string;
    headerAside?: React.ReactNode;
}

// TODO: Valentyn - move all this component into separate folder called 'shared'
// Avoid putting into one file multiple classes.

@observer
export class InviteStepsLayout extends TechseeBaseComponent<IStepLayoutProps> {
    renderInternal() {
        const {headerAside} = this.props;

        return (
            <div className={this.props.className}>
                {headerAside ? (
                    <div className='step-header-row'>
                        <div className='step-header-text'>
                            <h1 className='page-header'>{this.props.h1Text}</h1>
                            <h2 className='sub-header'>{this.props.h2Text}</h2>
                        </div>
                        {headerAside}
                    </div>
                ) : (
                    <>
                        <h1 className='page-header'>{this.props.h1Text}</h1>
                        <h2 className='sub-header'>{this.props.h2Text}</h2>
                    </>
                )}
                {this.props.children}
            </div>
        );
    }
}

interface PermissionsStepTabsProps {
    progressList: GuidenceProgressItem[];
    deviceType: InviteDeviceType;
    guidanceType: string;
    guidanceText: string;
    title: string;
    subTitle: string;
    stepClass: string;
    guidanceItems?: AgentGuidanceItem[];
    headerAside?: React.ReactNode;
}

@observer
export class PermissionsStepTabs extends TechseeBaseComponent<PermissionsStepTabsProps> {
    renderInternal() {
        const {
            progressList,
            deviceType,
            guidanceType,
            guidanceText,
            title,
            subTitle,
            stepClass,
            guidanceItems,
            headerAside
        } = this.props;

        const tabs = [
            {
                title: deviceType === InviteDeviceType.ios ? 'iOS' : 'Android',
                content: () => (
                    <GuidanceProgress
                        progressList={progressList}
                        guidanceItems={
                            guidanceItems
                                ? guidanceItems
                                : [
                                      {
                                          stepType: guidanceType,
                                          stepNum: 1,
                                          stepText: guidanceText,
                                          deviceType: deviceType
                                      }
                                  ]
                        }
                    />
                )
            }
        ];

        return (
            <InviteStepsLayout className={stepClass} h1Text={title} h2Text={subTitle} headerAside={headerAside}>
                <SimpleTabs key={`${guidanceType}-${deviceType}`} tabsList={tabs} uid={guidanceType + '-step-tab'} />
            </InviteStepsLayout>
        );
    }
}

interface IToolbarProps {
    wizardController: IInviteWizardController;
    translate: ITranslate;
}

@inject((stores: any) => ({
    wizardController: stores.inviteStore?.inviteFlowManager?.inviteWizardController,
    translate: stores.inviteStore?.localizationService?.translate
}))
@observer
export class SpeakerWifiStepToolbar extends TechseeBaseComponent<IToolbarProps> {
    private nextButtonRef: Nullable<ReactInstance> = null;

    constructor(props: IToolbarProps) {
        super(props);

        this.setNextBtnRef = this.setNextBtnRef.bind(this);
    }

    private get nextText() {
        const {translate, wizardController} = this.props;
        let translateButton = 'REACT.COMMON.BUTTONS.NEXT';
        const nextTextMap: any = {
            [AddressTypesEnum.EMAIL]: 'REACT.EMAIL.SEND_EMAIL',
            [AddressTypesEnum.SMS]: 'REACT.SMS.SEND_SMS',
            [AddressTypesEnum.WHATSAPP]: 'REACT.WHATSAPP.SEND_WHATSAPP'
        };

        if (
            (!wizardController.enableNewInviteWifiStep && wizardController.isOnSpekersStep()) ||
            wizardController.isOnWifiStep()
        ) {
            translateButton = nextTextMap[wizardController.addressType];
        }

        return translate(translateButton);
    }

    private setNextBtnRef(element: ReactInstance) {
        this.nextButtonRef = element;
    }

    private focusButton() {
        if (this.nextButtonRef) {
            // TODO: Wrong implementation!
            // See: https://github.com/yannickcr/eslint-plugin-react/blob/master/docs/rules/no-find-dom-node.md

            const btnElement: any = ReactDOM.findDOMNode(this.nextButtonRef);

            if (btnElement && btnElement.focus) {
                btnElement.focus();
            }
        }
    }

    componentDidMount(): void {
        this.focusButton();
    }

    renderInternal() {
        const {wizardController, translate} = this.props;
        const isBackVisible = wizardController.enableNewInviteSpeakersStep && wizardController.isOnWifiStep();
        const isNextVisible = !wizardController.isOnSmsStep();

        const ChangeNumberBtn = wizardController.isOnSmsStep() ? PrimaryButton : SecondaryButton;

        return (
            <React.Fragment>
                {isBackVisible && (
                    <SecondaryButton onClick={() => wizardController.onBackClick()}>
                        <Icon iconName={'arrow-back'} />
                        {translate('REACT.COMMON.BUTTONS.BACK')}
                    </SecondaryButton>
                )}
                {isNextVisible && (
                    <ChangeNumberBtn
                        onClick={() => wizardController.onChangeNumberClick()}
                        className={'change-number-btn'}>
                        {translate(
                            wizardController.addressType === AddressTypesEnum.EMAIL
                                ? 'REACT.INVITE.SPEAKER_WIFI.CHANGE_ADDRESS'
                                : 'REACT.INVITE.SPEAKER_WIFI.CHANGE_NUMBER'
                        )}
                    </ChangeNumberBtn>
                )}
                {isNextVisible && (
                    <PrimaryButton buttonRef={this.setNextBtnRef} onClick={() => wizardController.onNextClick()}>
                        {this.nextText}
                        <Icon iconName={'arrow-next'} />
                    </PrimaryButton>
                )}
            </React.Fragment>
        );
    }
}
